import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';

import { useWelcomeLetters } from '@views/pages/DataAnalysis/components/WelcomeLetters/hooks';
import { WelcomeLettersTable } from '@views/pages/DataAnalysis/components/WelcomeLetters/components';
import { theme } from '@store';
import { Select } from '@ui';
import { FC } from 'react';
import { Option } from '@common/types';

interface WelcomeLettersProps {
  programId: string;
  orgId: string;
  setOrgId: (orgId: string) => void;
  setProgramId: (programId: string) => void;
  organizationOptions: Option[];
  programOptions: Option[];
}

export const WelcomeLetters: FC<WelcomeLettersProps> = ({
  programId,
  orgId,
  setOrgId,
  setProgramId,
  organizationOptions,
  programOptions,
}) => {
  const {
    entries,
    setFilters,
    filters,
    totalCount,
    isLoading,
    patients_ready_to_welcome,
    handleChangeOrgId,
    handleChangeProgramId,
    handleGenerateLetters,
    generateIsPending,
  } = useWelcomeLetters({
    programId,
    orgId,
    setOrgId,
    setProgramId,
  });

  const columns = [
    {
      id: 'id',
      label: t('welcomeLetters.table.headers.id'),
      sortable: false,
      width: 150,
    },
    {
      id: 'organization_name',
      label: t('welcomeLetters.table.headers.organization_name'),
      sortable: false,
      width: 180,
    },
    {
      id: 'program_name',
      label: t('welcomeLetters.table.headers.program_name'),
      sortable: false,
      width: 180,
    },
    {
      id: 'date',
      label: t('welcomeLetters.table.headers.date'),
      sortable: true,
      width: 120,
    },
    {
      id: 'patients',
      label: t('welcomeLetters.table.headers.patients'),
      sortable: true,
      width: 130,
    },
    {
      id: 'download_date',
      label: t('welcomeLetters.table.headers.download_date'),
      sortable: true,
      width: 150,
    },
    {
      id: 'action',
      label: '',
      sortable: false,
      width: 50,
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'end',
          padding: '24px',
          backgroundColor: theme.palette.common.white,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          mb: '32px',
        }}
      >
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 2 }}>
            {t('welcomeLetters.welcomeLettersWaiting')}
          </Typography>
          <Typography variant='h3'>{patients_ready_to_welcome}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Select
            sx={{ width: '256px' }}
            name='welcomeLetters-organization'
            size='medium'
            label={t('welcomeLetters.filters.labels.organization')}
            value={orgId}
            onChange={handleChangeOrgId}
            options={organizationOptions}
          />

          <Select
            sx={{ width: '256px' }}
            name='welcomeLetters-program'
            size='medium'
            label={t('welcomeLetters.filters.labels.program')}
            value={programId}
            onChange={handleChangeProgramId}
            options={programOptions}
          />
          <Button
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              color: 'white',
              height: 42,
              padding: '8px 22px',
            }}
            color='secondary'
            variant='contained'
            onClick={handleGenerateLetters}
            disabled={generateIsPending || patients_ready_to_welcome === 0}
          >
            {t('welcomeLetters.generateBtn')}
          </Button>
        </Box>
      </Box>

      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        {t('welcomeLetters.allFile', { value: totalCount })}
      </Typography>

      <WelcomeLettersTable
        rows={entries}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        totalCount={totalCount}
        loading={isLoading}
      />
    </>
  );
};
